import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { TEXT_SIZE_SMALL, Txt } from 'components/Txt';
import { useConfirm } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import { Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  REMOTE_ACCOUNT_PASSWORD_CHANGE_SAVE,
} from 'store/actions';
import { selectUser } from 'store/auth/selectors';

export const ProfilePasswordScreen: React.FC<
  ModalScreenProps<ModalScreens.SETTINGSPROFILEPASSWORD>
> = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const user = useSelector(selectUser);
  const [oldPassword, setOldPassword] = React.useState<string>('');
  const [newPassword, setNewPassword] = React.useState<string>('');

  const handlePressNext = () => {
    if (oldPassword && newPassword) {
      dispatch(
        REMOTE_ACCOUNT_PASSWORD_CHANGE_SAVE({ newPassword, oldPassword }),
      );
    }
  };

  const hasValue = !!oldPassword || !!newPassword;

  const handleClose = () => {
    if (hasValue) {
      confirm({
        text: 'Changes will be lost if you continue',
        confirmText: 'Discard Changes',
        cancelText: 'Cancel',
        destructive: true,
        onConfirm: () => {
          dispatch(NAVIGATE_MODAL_BACK());
        },
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  useEsc(handleClose);

  if (!user) {
    return null;
  }

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Password"
          onPressCancelButton={handleClose}
          onPressSaveButton={
            oldPassword && newPassword ? handlePressNext : null
          }
        />
      }
    >
      <Card explanation="Your new password must be at least 12 characters long">
        <TextField
          textLeft="Current"
          textLeftPressableStyle={{ width: 60 }}
          textLeftStyle={{ fontSize: TEXT_SIZE_SMALL }}
          placeholder="Enter your current password"
          onChange={setOldPassword}
          secureTextEntry
          autoCapitalize="none"
          autoCorrect={false}
          onChangeDebounceMs={0}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
        <TextField
          textLeft="New"
          textLeftPressableStyle={{ width: 60 }}
          textLeftStyle={{ fontSize: TEXT_SIZE_SMALL }}
          placeholder="Enter a new password"
          onChange={setNewPassword}
          onSubmitEditing={Platform.OS === 'web' ? handlePressNext : undefined}
          autoComplete="newPassword"
          secureTextEntry
          autoCapitalize="none"
          autoCorrect={false}
          onChangeDebounceMs={0}
          onEsc={handleClose}
        />
      </Card>
      <CardGeneric>
        <Txt size="large" weight="bold">
          Make Your Password Long & Memorable
        </Txt>
        <Txt>
          We advise choosing a password that’s at least 12 characters long. Try
          using a sentence as your password: lyrics from a song, a quote from a
          film, a passage from a book, etc.
        </Txt>
      </CardGeneric>

      <CardGeneric>
        <Txt size="large" weight="bold">
          Don’t Use Personal Info
        </Txt>
        <Txt>
          Avoid including info about yourself that others might know or could
          easily find out, for example: your nickname, your pet’s name,
          important birthdays or years, numbers from your address, etc.
        </Txt>
      </CardGeneric>

      <CardGeneric>
        <Txt size="large" weight="bold">
          Don’t Use Common Words & Patterns
        </Txt>
        <Txt>
          Avoid words like ‘password’, ‘letmein’, ‘qwerty’, ‘123abc’, etc.
        </Txt>
      </CardGeneric>

      <CardGeneric>
        <Txt size="large" weight="bold">
          Passwordless
        </Txt>
        <Txt>
          Go passwordless instead! RiskBase emails you a 6 digit code when you
          sign in. Find this on &lsquo;Your Details and Security&rsquo; page.
        </Txt>
      </CardGeneric>

      <CardGeneric>
        <Txt size="large" weight="bold">
          Multi-Factor Authentication
        </Txt>
        <Txt>
          Add an extra layer of security to your account by enabling
          multi-factor authentication. Find this on &lsquo;Your Details and
          Security&rsquo; page.
        </Txt>
      </CardGeneric>
    </Page>
  );
};
